import html2canvas from 'html2canvas';

// Add FontFaceObserver for font loading
const waitForFont = async () => {
    return new Promise((resolve) => {
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => {
                // Additional wait to ensure Typekit fonts are properly loaded
                setTimeout(resolve, 100);
            });
        } else {
            // Fallback timeout if document.fonts is not supported
            setTimeout(resolve, 1000);
        }
    });
};

const loadFonts = async () => {
    try {
        // Load the fonts directly from Typekit
        const mediumFont = new FontFace(
            'futura-pt',
            'url(https://use.typekit.net/af/9b05f3/000000000000000000013365/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3) format("woff2")',
            { weight: '400' }
        );
        
        const demiFont = new FontFace(
            'futura-pt',
            'url(https://use.typekit.net/af/ae4f6c/000000000000000000013366/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3) format("woff2")',
            { weight: '600' }
        );

        // Load both fonts
        const [loadedMedium, loadedDemi] = await Promise.all([
            mediumFont.load(),
            demiFont.load()
        ]);

        // Add fonts to document
        document.fonts.add(loadedMedium);
        document.fonts.add(loadedDemi);

        // Wait for fonts to be ready
        await document.fonts.ready;
    } catch (err) {
        console.error('Error loading fonts:', err);
    }
};

/**
 * Creates and shares an image of a star
 * @param {Object} star - The star object containing name, dob, pob, etc.
 * @param {number} weeksLoved - Number of weeks the star has been loved
 * @param {string} usid - The unique identifier for the star
 * @param {number|string} starType - The type of star (used for background image)
 * @returns {Promise<{imageUrl: string, imageBlob: Blob}>}
 */
export const createAndShareStarImage = async (star, weeksLoved, usid, starType) => {
    try {
        // Create parent container
        const parentContainer = document.createElement('div');
        parentContainer.style.position = 'fixed';
        parentContainer.style.top = '0';
        parentContainer.style.left = '0';
        parentContainer.style.width = '100%';
        parentContainer.style.height = '100%';
        parentContainer.style.zIndex = '-1000';
        parentContainer.style.pointerEvents = 'none';
        
        // Create the main content container
        const contentElement = document.createElement('div');
        contentElement.style.width = '1080px';
        contentElement.style.height = '1920px';
        contentElement.style.position = 'relative';
        contentElement.style.overflow = 'hidden';
        contentElement.style.backgroundColor = '#000';
        contentElement.style.backgroundImage = `url(/assets/images/backgrounds/background-${starType}.jpg)`;
        contentElement.style.backgroundSize = 'cover';
        contentElement.style.backgroundPosition = 'center';
        contentElement.style.display = 'flex';
        contentElement.style.flexDirection = 'column';
        contentElement.style.justifyContent = 'space-between';
        contentElement.style.alignItems = 'center';
        contentElement.style.padding = '80px 40px';
        contentElement.style.boxSizing = 'border-box';
        contentElement.style.fontFamily = 'futura-pt, Arial, Helvetica, sans-serif';
        
        // Logo
        const logoHolder = document.createElement('div');
        logoHolder.style.backgroundImage = `url(/assets/images/StillLovedLogo.png)`;
        logoHolder.style.backgroundSize = 'contain';
        logoHolder.style.backgroundPosition = 'center';
        logoHolder.style.backgroundRepeat = 'no-repeat';
        logoHolder.style.width = '400px';
        logoHolder.style.height = '93px';
        logoHolder.style.paddingTop = '20px';
        contentElement.appendChild(logoHolder);
        
        // Main title with name, weeks, and still loved
        const nameText = star.name.toUpperCase();
        const weeksText = `${weeksLoved} WEEKS`;
        const stillLovedText = 'STILL LOVED';
        
        const calculateNameFontSize = (name) => {
            const baseSize = 110;
            const maxChars = 12;
            const minSize = 60;
            
            if (name.length <= maxChars) return baseSize;
            
            const scaledSize = Math.max(minSize, baseSize * (maxChars / name.length));
            return Math.floor(scaledSize);
        };

        const nameFontSize = calculateNameFontSize(nameText);
        
        const titleElement = document.createElement('div');
        titleElement.style.textAlign = 'center';
        titleElement.style.paddingTop = '190px';
        titleElement.innerHTML = `
            <div style="color:rgba(255, 255, 255, 0.8); font-weight: 700; font-size: ${nameFontSize}px; line-height: 0.8; margin-bottom: 40px; text-transform: uppercase; text-shadow: 0 0 20px #ffffff;">${nameText}</div>
            <div style="color: rgba(255, 255, 255, 0.8); font-size: 80px; line-height: 0.7; margin-bottom: 17px; text-transform: uppercase; font-weight: 400;">${weeksText}</div>
            <div style="color: rgba(255, 255, 255, 0.8); font-size: 80px; line-height: 0.7; text-transform: uppercase; font-weight: 400;">${stillLovedText}</div>
        `;
        contentElement.appendChild(titleElement);
        
        // Footer text
        const footerText = document.createElement('div');
        footerText.style.color = 'white';
        footerText.style.fontSize = '30px';
        footerText.style.textAlign = 'center';
        footerText.style.lineHeight = '0.8';
        footerText.style.fontWeight = '400';
        footerText.style.paddingBottom = '100px';
        footerText.innerHTML = 'Add your star at StillLoved.co.uk';
        contentElement.appendChild(footerText);
        
        parentContainer.appendChild(contentElement);
        document.body.appendChild(parentContainer);
        
        try {
            //console.log('Starting image capture...');
            
            // Wait for background image to load
            await new Promise((resolve) => {
                const img = new Image();
                img.onload = resolve;
                img.src = '/assets/images/backgrounds/background-generic.jpg';
            });

            // Use html2canvas with specific options for better font handling
            const canvas = await html2canvas(contentElement, {
                scale: 2, // Equivalent to pixelRatio: 2
                useCORS: true,
                allowTaint: true,
                backgroundColor: null,
                logging: true, // Enable logging for debugging
                onclone: (clonedDoc) => {
                    // Force font loading in the cloned document
                    const style = clonedDoc.createElement('style');
                    style.textContent = `
                        @import url('https://use.typekit.net/prl1dkg.css');
                        * { font-family: 'futura-pt', Arial, Helvetica, sans-serif !important; }
                    `;
                    clonedDoc.head.appendChild(style);
                }
            });

            // Convert canvas to PNG
            const png = canvas.toDataURL('image/png');
            
            //console.log('Image captured successfully');

            // Convert the base64 image to a blob
            const response = await fetch(png);
            const blob = await response.blob();

            // Create a File object from the Blob
            const file = new File([blob], `${star.name}-star.png`, { type: 'image/png' });

            // Return both the URL and blob for different sharing methods
            return {
                imageUrl: png,
                imageFile: file
            };
        } catch (innerErr) {
            console.error('Error during image capture:', innerErr);
            throw innerErr;
        } finally {
            document.body.removeChild(parentContainer);
        }
    } catch (err) {
        console.error('Failed to create image:', err);
        throw err;
    }
};

/**
 * Share the star using Web Share API or fallback to custom dialog
 * @param {Object} shareData - The data to be shared
 * @param {File} shareData.file - The image file to share
 * @param {string} shareData.title - The title of the share
 * @param {string} shareData.text - The text to share
 * @param {string} shareData.url - The URL to share
 * @returns {Promise<boolean>} - Returns true if Web Share API was used, false if fallback
 */
export const shareContent = async ({ file, title, text, url }) => {
    if (navigator.share && navigator.canShare) {
        const shareData = {
            title,
            text,
            url,
            files: file ? [file] : undefined
        };

        // Check if the browser can share files
        if (navigator.canShare(shareData)) {
            try {
                await navigator.share(shareData);
                return true;
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Error sharing:', err);
                }
                return false;
            }
        }
    }
    
    // Return false to indicate fallback UI should be shown
    return false;
}; 